import type { Deal, LostToLenderDetails, LostToLenderReason } from "../types/deal";

/** Reads unified notes; falls back to legacy API keys on read only. */
export const getLostToLenderNotes = (
  details: LostToLenderDetails | null | undefined,
): string => {
  if (!details) return "";
  if (typeof details.notes === "string" && details.notes.trim()) {
    return details.notes.trim();
  }
  const legacy =
    details.lost_to_lender_notes ?? details.winning_lender_terms_notes ?? "";
  return typeof legacy === "string" ? legacy.trim() : "";
};

/** Reads merged LTC/LTV; falls back to legacy API keys on read only. */
export const getWinningLenderLtcLtv = (
  details: LostToLenderDetails | null | undefined,
): number | null => {
  if (!details) return null;
  const current = details.winning_lender_ltc_ltv;
  if (current != null && !Number.isNaN(current)) return current;
  const ltv = details.winning_lender_ltv;
  if (ltv != null && !Number.isNaN(ltv)) return ltv;
  const ltc = details.winning_lender_ltc;
  if (ltc != null && !Number.isNaN(ltc)) return ltc;
  return null;
};

/** Strips deprecated keys; writes only current lost-to-lender detail fields for PUT. */
export const sanitizeLostToLenderDetailsForPut = (
  details: LostToLenderDetails,
): LostToLenderDetails => {
  const notesValue = getLostToLenderNotes(details);
  const notes = notesValue === "" ? null : notesValue;
  const ltcLtv = getWinningLenderLtcLtv(details);

  return {
    id: details.id,
    deal_id: details.deal_id,
    lost_to_lender_reason_id:
      details.lost_to_lender_reason?.id ?? details.lost_to_lender_reason_id,
    lost_to_lender_reason: details.lost_to_lender_reason,
    notes,
    winning_lender_name: details.winning_lender_name ?? null,
    winning_lender_loan_amount: details.winning_lender_loan_amount ?? null,
    winning_lender_margin: details.winning_lender_margin ?? null,
    winning_lender_ltc_ltv: ltcLtv,
    winning_lender_arrangement_fee: details.winning_lender_arrangement_fee ?? null,
    winning_lender_initial_loan_term:
      details.winning_lender_initial_loan_term ?? null,
  };
};

export const isLostToOtherLenderStatus = (statusName: unknown): boolean => {
  const s = String(statusName ?? "").toLowerCase();
  return s.includes("lost to other lender");
};

export const isKilledStatus = (statusName: unknown): boolean => {
  const s = String(statusName ?? "").trim().toLowerCase();
  return s === "killed";
};

export const isKilledOrLostStatus = (statusName: unknown): boolean => {
  const s = String(statusName ?? "").toLowerCase();
  return s.includes("killed") || s.includes("lost to other lender");
};

export const getKillReasonName = (deal: {
  kill_reason?: { kill_reason?: string } | string | null;
}): string => {
  const kr = deal?.kill_reason;
  if (typeof kr === "string") return kr;
  if (kr && typeof kr === "object" && typeof kr.kill_reason === "string") {
    return kr.kill_reason;
  }
  return "";
};

export const hasKillReason = (deal: {
  kill_reason?: { kill_reason?: string; id?: number } | string | null;
}): boolean => {
  const kr = deal?.kill_reason;
  if (kr == null) return false;
  if (typeof kr === "string") return kr.trim() !== "";
  if (typeof kr === "object") {
    if (typeof kr.id === "number") return true;
    return Boolean(kr.kill_reason && String(kr.kill_reason).trim() !== "");
  }
  return false;
};

export const hasLostToLenderCapture = (
  details: LostToLenderDetails | null | undefined,
): boolean => {
  if (!details) return false;
  const reasonId =
    details.lost_to_lender_reason?.id ?? details.lost_to_lender_reason_id;
  if (typeof reasonId === "number") return true;
  return getLostToLenderNotes(details) !== "";
};

export const buildLostToLenderDetailsPayload = (
  existing: LostToLenderDetails | null | undefined,
  patch: Partial<LostToLenderDetails>,
): LostToLenderDetails => {
  const merged: LostToLenderDetails = {
    ...(existing ?? {}),
    ...patch,
  };

  if (patch.lost_to_lender_reason !== undefined) {
    merged.lost_to_lender_reason = patch.lost_to_lender_reason;
    merged.lost_to_lender_reason_id =
      patch.lost_to_lender_reason?.id ?? undefined;
  }

  return sanitizeLostToLenderDetailsForPut(merged);
};

export const killReasonsEqual = (
  a: { id?: number } | string | null | undefined,
  b: { id?: number } | string | null | undefined,
): boolean => {
  const aId =
    a == null
      ? null
      : typeof a === "object"
        ? (a.id ?? null)
        : null;
  const bId =
    b == null
      ? null
      : typeof b === "object"
        ? (b.id ?? null)
        : null;
  return aId === bId;
};

const normalizeLostDetailsForCompare = (
  details: LostToLenderDetails | null | undefined,
) => {
  const reasonId =
    details?.lost_to_lender_reason?.id ?? details?.lost_to_lender_reason_id ?? null;
  const text = (value: string | null | undefined) => {
    const trimmed = (value ?? "").trim();
    return trimmed === "" ? null : trimmed;
  };
  const num = (value: number | null | undefined) =>
    value == null || Number.isNaN(value) ? null : value;

  return {
    reasonId,
    notes: text(getLostToLenderNotes(details) || details?.notes),
    winning_lender_name: text(details?.winning_lender_name),
    winning_lender_loan_amount: num(details?.winning_lender_loan_amount),
    winning_lender_margin: num(details?.winning_lender_margin),
    winning_lender_ltc_ltv: num(getWinningLenderLtcLtv(details)),
    winning_lender_arrangement_fee: num(details?.winning_lender_arrangement_fee),
    winning_lender_initial_loan_term: num(
      details?.winning_lender_initial_loan_term,
    ),
  };
};

export const lostToLenderDetailsEqual = (
  a: LostToLenderDetails | null | undefined,
  b: LostToLenderDetails | null | undefined,
): boolean =>
  JSON.stringify(normalizeLostDetailsForCompare(a)) ===
  JSON.stringify(normalizeLostDetailsForCompare(b));

export const hasKilledDealRowEdits = (current: Deal, original: Deal): boolean =>
  !killReasonsEqual(current.kill_reason, original.kill_reason);

export const hasLostDealRowEdits = (current: Deal, original: Deal): boolean => {
  if (!killReasonsEqual(current.kill_reason, original.kill_reason)) return true;
  return !lostToLenderDetailsEqual(
    current.lost_to_lender_details,
    original.lost_to_lender_details,
  );
};

export const normalizeLostToLenderReason = (
  value: unknown,
  reasons: LostToLenderReason[],
): LostToLenderReason | null => {
  if (value == null) return null;
  if (typeof value === "object" && (value as LostToLenderReason).id == null) {
    return null;
  }
  const id =
    typeof value === "object" &&
    typeof (value as LostToLenderReason).id === "number"
      ? (value as LostToLenderReason).id
      : null;
  if (id == null) return null;
  return reasons.find((r) => r.id === id) ?? (value as LostToLenderReason);
};
